import React, { useState } from 'react';
import { HelpCircle, X, AlertTriangle, Timer, Clock, Phone } from 'lucide-react';
import ReactDOM from 'react-dom';

const EXPLAINER_CONFIG = {
  uncertain: {
    icon:      AlertTriangle,
    iconBg:    'rgba(245,166,35,0.12)',
    iconColor: '#f5a623',
    accent:    '#f5a623',
    title:     "Qu'est-ce que \"Incertain\" ?",
    what:      "Le commerçant n'a pas confirmé son statut récemment. Il a peut-être quitté son magasin sans mettre à jour l'application.",
    what_do: [
      { icon: Phone, text: 'Appelez le commerçant si un numéro est disponible.' },
      { icon: Clock, text: 'Repassez un peu plus tard — il sera peut-être ouvert.' },
    ],
  },
  be_back: {
    icon:      Timer,
    iconBg:    'rgba(59,130,246,0.12)',
    iconColor: '#3b82f6',
    accent:    '#3b82f6',
    title:     "Qu'est-ce que \"De retour bientôt\" ?",
    what:      "Le commerçant a signalé une absence temporaire et a indiqué l'heure approximative de son retour.",
    what_do: [
      { icon: Timer, text: 'Patientez — un compte à rebours indique quand il revient.' },
      { icon: Phone, text: "Vous pouvez aussi l'appeler pour confirmer." },
    ],
  },
};

const ExplainerModal = ({ cfg, onClose }) => {
  const modal = (
    <div
      onClick={e => { e.stopPropagation(); onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.50)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.25rem',
        animation: 'seOverlayIn 0.15s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          borderRadius: 'var(--r-xl)',
          padding: '1.25rem',
          width: '100%', maxWidth: 310,
          boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
          animation: 'sePopIn 0.18s cubic-bezier(0.34,1.4,0.64,1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.85rem' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 'var(--r-md)',
            background: cfg.iconBg, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <cfg.icon size={19} color={cfg.iconColor} strokeWidth={2}/>
          </div>
          <div style={{ flex: 1, fontWeight: 800, fontSize: '0.86rem', color: 'var(--text)', lineHeight: 1.25 }}>
            {cfg.title}
          </div>
          <button onClick={onClose} style={{
            background: 'var(--bg-elevated)', border: 'none',
            borderRadius: '50%', width: 26, height: 26,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0,
          }}>
            <X size={12}/>
          </button>
        </div>

        <p style={{ fontSize: '0.77rem', color: 'var(--text-2)', lineHeight: 1.6, marginBottom: '0.8rem' }}>
          {cfg.what}
        </p>

        <div style={{
          background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
          padding: '0.55rem 0.7rem',
          display: 'flex', flexDirection: 'column', gap: '0.4rem',
          marginBottom: '0.9rem',
        }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.05rem' }}>
            Que faire ?
          </div>
          {cfg.what_do.map(({ icon: Icon, text }, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Icon size={12} color={cfg.iconColor} strokeWidth={2.2} style={{ flexShrink: 0 }}/>
              <span style={{ fontSize: '0.74rem', color: 'var(--text-2)', fontWeight: 500 }}>{text}</span>
            </div>
          ))}
        </div>

        <button onClick={onClose} style={{
          width: '100%', padding: '0.62rem',
          borderRadius: 'var(--r-lg)',
          border: `1.5px solid ${cfg.accent}35`,
          background: `${cfg.accent}10`,
          color: cfg.accent, fontWeight: 700,
          fontSize: '0.84rem', cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Compris
        </button>
      </div>

      <style>{`
        @keyframes seOverlayIn { from{opacity:0} to{opacity:1} }
        @keyframes sePopIn { from{opacity:0;transform:scale(.93)} to{opacity:1;transform:scale(1)} }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

const StatusExplainer = ({ type, size = 'sm', inline = false }) => {
  const [open, setOpen] = useState(false);
  const cfg = EXPLAINER_CONFIG[type];
  if (!cfg) return null;
  const iconSize = size === 'sm' ? 13 : 16;

  return (
    <>
      <button
        onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        aria-label="En savoir plus sur ce statut"
        style={{
          display: inline ? 'inline-flex' : 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'none', border: 'none', padding: '0.1rem',
          cursor: 'pointer', color: cfg.iconColor, opacity: 0.75,
          transition: 'opacity 0.15s', flexShrink: 0, verticalAlign: 'middle',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
        onMouseLeave={e => e.currentTarget.style.opacity = '0.75'}
      >
        <HelpCircle size={iconSize} strokeWidth={2.5}/>
      </button>

      {open && <ExplainerModal cfg={cfg} onClose={() => setOpen(false)} />}
    </>
  );
};

export default StatusExplainer;